import React from 'react';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import AstrologerRoute from './components/AstrologerRoute';
import VedicThemeProvider from './components/vedic/theme/VedicThemeProvider';
import withFreeToolAuth from './components/auth/withFreeToolAuth';

// Layouts
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AstrologerCTA from './components/layout/AstrologerCTA';

// Admin Pages
import InvitationsPage from './pages/admin/InvitationsPage';

// Astrologer Pages
import VerifyPage from './pages/astrologer/VerifyPage';
import RegisterPage from './pages/astrologer/RegisterPage';
import PasswordCreationForm from './components/astrologer/PasswordCreationForm';
import AstrologerDashboard from './components/astrologer/AstrologerDashboard';

// Auth Pages
import Login from './components/pages/Login';
import Register from './components/pages/Register';

// Public Pages
import LandingPage from './components/pages/LandingPage';
import MainPage from './components/pages/MainPage';
import Courses from './components/pages/Courses';
import HomePage from './components/pages/HomePage';
import ChatWithAstrologer from './components/pages/ChatWithAstrologer';
import TalkToAstrologer from './components/pages/TalkToAstrologer';
import FreeKundli from './components/pages/FreeKundli';
import KundliMatching from './components/pages/KundliMatching';
import TodayPanchangam from './components/pages/TodayPanchangam';
import DailyBalance from './components/pages/DailyBalance';
import KalaSakthiDashboard from './components/pages/KalaSakthiDashboard';
import DailyTimings from './components/pages/DailyTimings';
import PlanetaryHora from './components/pages/PlanetaryHora';
import AbhijitMuhurta from './components/pages/AbhijitMuhurta';
import MuhurtaChecker from './components/pages/MuhurtaChecker';
import TenPorutham from './components/pages/TenPorutham';
import RamaShalaka from './components/pages/RamaShalaka';
import GaneshaPrashnavali from './components/pages/GaneshaPrashnavali';
import HanumanPrashnavali from './components/pages/HanumanPrashnavali';
import ChanakyaNiti from './components/pages/ChanakyaNiti';
import VedicTools from './components/pages/VedicTools';
import KPChart from './components/pages/KPChart';
import DailyHoroscope from './components/pages/DailyHoroscope';
import AstrologerList from './components/astrologer/AstrologerList';
import AstrologerProfile from './components/astrologer/AstrologerProfile';
import EditProfile from './components/EditProfile';
import CourseDetail from './components/pages/CourseDetail';
import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Enhanced components for slot booking and real-time chat
import TalkToAstrologerEnhanced from './components/pages/TalkToAstrologerEnhanced';
import ChatWithAstrologerEnhanced from './components/pages/ChatWithAstrologerEnhanced';
import AstrologerChat from './components/pages/AstrologerChat';
import UserDashboard from './components/pages/UserDashboard';
import CallHistory from './components/pages/CallHistory';

// Calendar v3 - Availability and Booking Pages
import AstrologerAvailability from './pages/AstrologerAvailability';
import UserBooking from './pages/UserBooking';
import MyBookings from './pages/MyBookings';

// Wallet
import WalletDashboard from './components/wallet/WalletDashboard';
import AdminWithdrawals from './components/wallet/AdminWithdrawals';

// Earnings / Revenue
import EarningsDashboard from './pages/astrologer/EarningsDashboard';
import AboutMePage from './pages/astrologer/AboutMePage';
import RevenueDashboard from './pages/admin/RevenueDashboard';

// Ledger
import LedgerDashboard from './pages/admin/LedgerDashboard';
import JournalDetail from './pages/admin/JournalDetail';

// Monitoring
import MonitorPlatform from './pages/admin/MonitorPlatform';

// Broadcast
import { BroadcastPage } from './pages/admin';

// Notifications
import NotificationCenter from './components/notifications/NotificationCenter';
import NotificationSettings from './components/notifications/NotificationSettings';

// Admin Management
import UsersPage from './pages/admin/UsersPage';
import ConsultationsPage from './pages/admin/ConsultationsPage';
import AstrologersPage from './pages/admin/AstrologersPage';

// Free tool auth-gated wrappers (soft gate for unauthenticated visitors)
const ProtectedFreeKundli = withFreeToolAuth({
  toolName: 'Free Horoscope',
  toolDescription: 'Get your complete Vedic birth chart with planetary positions, dashas, yogas, and Ashtakavarga analysis.',
  toolIcon: '\u{1F4DC}',
})(FreeKundli);

const ProtectedTodayPanchangam = withFreeToolAuth({
  toolName: 'Today Panchangam',
  toolDescription: 'View today\'s Tithi, Nakshatra, Yoga, Karana, and Vara — the five essential elements of the Vedic calendar.',
  toolIcon: '\u{1F549}️',
})(TodayPanchangam);

const ProtectedDailyHoroscope = withFreeToolAuth({
  toolName: 'Daily Horoscope',
  toolDescription: 'Read your personalized daily horoscope based on your Moon sign (Rasi).',
  toolIcon: '⭐',
})(DailyHoroscope);

const ProtectedDailyBalance = withFreeToolAuth({
  toolName: 'KalaShakti Daily Balance',
  toolDescription: 'Get your personalized 0-100 daily score with hourly and weekly forecasts.',
  toolIcon: '⚡',
})(DailyBalance);

const ProtectedKalaSakthiDashboard = withFreeToolAuth({
  toolName: 'KalaSakthi V2',
  toolDescription: 'Your life blueprint — 12 house scores, 5-category radar chart, and detailed drill-down analysis.',
  toolIcon: '\u{1F531}',
})(KalaSakthiDashboard);

const ProtectedDailyTimings = withFreeToolAuth({
  toolName: 'Daily Timings',
  toolDescription: 'Find Rahu Kalam, Yamagandam, Gulika Kalam, and Choghadiya timings for today.',
  toolIcon: '\u{1F550}',
})(DailyTimings);

const ProtectedPlanetaryHora = withFreeToolAuth({
  toolName: 'Planetary Hora',
  toolDescription: 'See the 24 planetary hours for today — know which planet rules each hour of the day.',
  toolIcon: '\u{1FA90}',
})(PlanetaryHora);

const ProtectedAbhijitMuhurta = withFreeToolAuth({
  toolName: 'Abhijit Muhurta',
  toolDescription: 'Find the most auspicious 48-minute golden window around noon for important activities.',
  toolIcon: '✨',
})(AbhijitMuhurta);

const ProtectedMuhurtaChecker = withFreeToolAuth({
  toolName: 'Muhurta Checker',
  toolDescription: 'Score any date and time for marriage, travel, business, and other important events.',
  toolIcon: '\u{1F4C5}',
})(MuhurtaChecker);

const ProtectedKundliMatching = withFreeToolAuth({
  toolName: 'Horoscope Matching',
  toolDescription: 'Ashtakoot gun milan — check marriage compatibility with the 8-koota, 36-point North Indian system.',
  toolIcon: '\u{1F48D}',
})(KundliMatching);

const ProtectedTenPorutham = withFreeToolAuth({
  toolName: '10 Porutham',
  toolDescription: 'Tamil marriage compatibility — check Rajju, Vedha, Nadi, and 7 more poruthams.',
  toolIcon: '\u{1F491}',
})(TenPorutham);

const ProtectedKPChart = withFreeToolAuth({
  toolName: 'KP Chart',
  toolDescription: 'Krishnamurti Paddhati — Sub-Lord analysis, Placidus houses, and significator tables.',
  toolIcon: '\u{1F3AF}',
})(KPChart);

// Define routes with their respective layouts and components
const routes = [
  // Public routes with header/footer layout
  {
    path: '/',
    element: (
      <>
        <Header />
        <AstrologerCTA />
        <LandingPage />
        <Footer />
      </>
    )
  },
  {
    path: '/consultation',
    element: (
      <>
        <Header />
        <AstrologerCTA />
        <MainPage />
        <Footer />
      </>
    )
  },
  {
    path: '/courses',
    element: (
      <>
        <Header />
        <AstrologerCTA />
        <Courses />
        <Footer />
      </>
    )
  },
  {
    path: '/home',
    element: <HomePage />
  },
  {
    path: '/chat-with-astrologer',
    element: <ChatWithAstrologer />
  },
  {
    path: '/chat/:consultationId',
    element: <PrivateRoute><ChatWithAstrologerEnhanced /></PrivateRoute>
  },
  {
    path: '/consultation/:consultationId/chat',
    element: <PrivateRoute><ChatWithAstrologerEnhanced /></PrivateRoute>
  },
  {
    path: '/talk-to-astrologer',
    element: (
      <>
        <Header />
        <AstrologerCTA />
        <TalkToAstrologerEnhanced />
      </>
    )
  },
  {
    path: '/talk-to-astrologer-old',
    element: <TalkToAstrologer />
  },
  {
    path: '/free-kundli',
    element: <VedicThemeProvider><ProtectedFreeKundli /></VedicThemeProvider>
  },
  {
    path: '/kundli-matching',
    element: <VedicThemeProvider><ProtectedKundliMatching /></VedicThemeProvider>
  },
  {
    path: '/today-panchangam',
    element: <ProtectedTodayPanchangam />
  },
  {
    path: '/daily-balance',
    element: <VedicThemeProvider><ProtectedDailyBalance /></VedicThemeProvider>
  },
  {
    path: '/kalasakthi',
    element: <VedicThemeProvider><ProtectedKalaSakthiDashboard /></VedicThemeProvider>
  },
  {
    path: '/daily-timings',
    element: <VedicThemeProvider><ProtectedDailyTimings /></VedicThemeProvider>
  },
  {
    path: '/hora',
    element: <VedicThemeProvider><ProtectedPlanetaryHora /></VedicThemeProvider>
  },
  {
    path: '/abhijit-muhurta',
    element: <VedicThemeProvider><ProtectedAbhijitMuhurta /></VedicThemeProvider>
  },
  {
    path: '/muhurta-check',
    element: <VedicThemeProvider><ProtectedMuhurtaChecker /></VedicThemeProvider>
  },
  {
    path: '/porutham',
    element: <VedicThemeProvider><ProtectedTenPorutham /></VedicThemeProvider>
  },
  {
    path: '/rama-shalaka',
    element: <VedicThemeProvider><RamaShalaka /></VedicThemeProvider>
  },
  {
    path: '/ganesha-prashnavali',
    element: <VedicThemeProvider><GaneshaPrashnavali /></VedicThemeProvider>
  },
  {
    path: '/hanuman-prashnavali',
    element: <VedicThemeProvider><HanumanPrashnavali /></VedicThemeProvider>
  },
  {
    path: '/chanakya-niti',
    element: <VedicThemeProvider><ChanakyaNiti /></VedicThemeProvider>
  },
  {
    path: '/vedic-tools',
    element: <VedicThemeProvider><VedicTools /></VedicThemeProvider>
  },
  {
    path: '/kp-chart',
    element: <VedicThemeProvider><ProtectedKPChart /></VedicThemeProvider>
  },
  {
    path: '/daily-horoscope',
    element: <><Header /><ProtectedDailyHoroscope /><Footer /></>
  },

  // Auth routes
  {
    path: '/login',
    element: <><Header /><Login /></>
  },
  {
    path: '/register',
    element: <><Header /><Register /></>
  },

  // User-only protected routes
  {
    path: '/dashboard',
    element: <PrivateRoute><><Header /><UserDashboard /></></PrivateRoute>
  },
  {
    path: '/profile/edit',
    element: <PrivateRoute><><Header /><EditProfile /></></PrivateRoute>
  },
  {
    path: '/book-consultation',
    element: <PrivateRoute><><Header /><UserBooking /></></PrivateRoute>
  },
  {
    path: '/book-consultation/:astrologerId',
    element: <PrivateRoute><><Header /><UserBooking /></></PrivateRoute>
  },
  {
    path: '/my-bookings',
    element: <PrivateRoute><><Header /><MyBookings /></></PrivateRoute>
  },
  {
    path: '/wallet',
    element: <PrivateRoute><><Header /><WalletDashboard /></></PrivateRoute>
  },
  {
    path: '/call-history',
    element: <PrivateRoute><><Header /><CallHistory /></></PrivateRoute>
  },
  {
    path: '/notifications',
    element: <PrivateRoute><><Header /><NotificationCenter /></></PrivateRoute>
  },
  {
    path: '/notifications/settings',
    element: <PrivateRoute><><Header /><NotificationSettings /></></PrivateRoute>
  },

  // Admin routes
  {
    path: '/admin',
    element: <AdminRoute><RevenueDashboard /></AdminRoute>,
  },
  {
    path: '/admin/dashboard',
    element: <AdminRoute><RevenueDashboard /></AdminRoute>
  },
  {
    path: '/admin/invitations',
    element: <AdminRoute><InvitationsPage /></AdminRoute>
  },
  {
    path: '/admin/withdrawals',
    element: <AdminRoute><AdminWithdrawals /></AdminRoute>
  },
  {
    path: '/admin/revenue',
    element: <AdminRoute><RevenueDashboard /></AdminRoute>
  },
  {
    path: '/admin/ledger',
    element: <AdminRoute><LedgerDashboard /></AdminRoute>
  },
  {
    path: '/admin/ledger/journals/:id',
    element: <AdminRoute><JournalDetail /></AdminRoute>
  },
  {
    path: '/admin/users',
    element: <AdminRoute><UsersPage /></AdminRoute>
  },
  {
    path: '/admin/consultations',
    element: <AdminRoute><ConsultationsPage /></AdminRoute>
  },
  {
    path: '/admin/astrologers',
    element: <AdminRoute><AstrologersPage /></AdminRoute>
  },
  {
    path: '/admin/monitor',
    element: <AdminRoute><MonitorPlatform /></AdminRoute>
  },
  {
    path: '/admin/push-notifications',
    element: <AdminRoute><BroadcastPage /></AdminRoute>
  },

  // Astrologer routes (role-guarded)
  {
    path: '/astrologer/dashboard',
    element: <AstrologerRoute><AstrologerDashboard /></AstrologerRoute>
  },
  {
    path: '/astrologer/chat/:consultationId',
    element: <AstrologerRoute><AstrologerChat /></AstrologerRoute>
  },
  {
    path: '/astrologer/availability',
    element: <AstrologerRoute><AstrologerAvailability /></AstrologerRoute>
  },
  {
    path: '/astrologer/earnings',
    element: <AstrologerRoute><EarningsDashboard /></AstrologerRoute>
  },
  {
    path: '/astrologer/about',
    element: <AstrologerRoute><AboutMePage /></AstrologerRoute>
  },

  // Public astrologer listing/profile
  {
    path: '/astrologers',
    element: (
      <>
        <Header />
        <AstrologerCTA />
        <AstrologerList />
        <Footer />
      </>
    )
  },
  {
    path: '/astrologer/:id',
    element: (
      <>
        <Header />
        <AstrologerCTA />
        <AstrologerProfile />
        <Footer />
      </>
    )
  },
  {
    path: '/course/:id',
    element: <CourseDetail />
  },

  // Invitation verification & registration routes (public — astrologer onboarding)
  {
    path: '/verify/:token',
    element: <VerifyPage />
  },
  {
    path: '/create-password',
    element: <PasswordCreationForm />
  },
  {
    path: '/register/astrologer',
    element: <RegisterPage />
  },

  // Not found and unauthorized routes
  {
    path: '/unauthorized',
    element: <UnauthorizedPage />
  },
  {
    path: '/privacy',
    element: (
      <>
        <Header />
        <PrivacyPolicy />
        <Footer />
      </>
    )
  },
  {
    path: '/terms',
    element: (
      <>
        <Header />
        <TermsOfService />
        <Footer />
      </>
    )
  },
  {
    path: '*',
    element: <NotFoundPage />
  }
];

export default routes;
